// frontend/src/constants/orgFieldLimits.js
//
// The length check every SaaS form with a server-side text bound uses, plus the
// column widths for the free-text settings Store Settings writes through
// PATCH /auth/stores/settings/. Other forms keep their limits beside the form,
// each citing the server field it mirrors.
//
// Inputs carry no `maxLength`: a capped input stops the cursor and cuts a
// pasted value short without a word, so the form keeps the value, names the
// field that is too long, and withholds its submit (`lengthLimits`).
//
// The server stays the authority — e.g. `_bounded_text` in
// backend/authentication/views/stores.py answers an over-long value with a 400.
// These limits exist so the merchant is told which field is wrong and why,
// before a round trip, instead of receiving a generic save failure.

/** core.models.Organization column widths. */
export const ORG_FIELD_MAX = {
  name: 255,
};

/** core.models.Store `company_*` column widths — the `company` section. */
export const COMPANY_FIELD_MAX = {
  name: 255,
  address_line1: 255,
  address_line2: 255,
  city: 100,
  state_province: 100,
  postal_code: 20,
  phone: 20,
  email: 254,
};

/** core.models.Store — editable for manual stores only. */
export const STORE_FIELD_MAX = {
  name: 255,
  contact_email: 254,
};

/**
 * How long `text` is the way the server measures it: Python's len() counts
 * code points, so an emoji is one character here too, not two UTF-16 units.
 *
 * @param {string} text
 */
export function charLength(text) {
  return [...text].length;
}

/**
 * The fields of `form` whose value is longer than its limit.
 *
 * Trailing and leading whitespace is discounted because the server discounts
 * it too (`str(raw).strip()` before the length test). The two trims are not
 * asserted to be character-for-character identical across every Unicode
 * whitespace class — the server stays authoritative and will still answer 400
 * — but they agree on everything a merchant types.
 *
 * @param {Record<string, string>} form
 * @param {Record<string, number>} limits
 * @returns {string[]} field names, in `limits` order
 */
export function overlongFields(form, limits) {
  return Object.keys(limits).filter(
    (field) => charLength(String(form?.[field] ?? '').trim()) > limits[field],
  );
}

/**
 * The length state of one form: `propsFor(field)` gives a TextField's error
 * props when that field is too long (naming its limit), and `tooLong` says
 * whether any field is, for the caller to withhold its submit.
 *
 * @param {Record<string, string>} form
 * @param {Record<string, number>} limits
 * @param {(key: string, opts?: object) => string} t
 */
export function lengthLimits(form, limits, t) {
  const overlong = overlongFields(form, limits);
  return {
    tooLong: overlong.length > 0,
    propsFor: (field) => (overlong.includes(field)
      ? { error: true, helperText: t('common.tooLong', { max: limits[field] }) }
      : {}),
  };
}
